import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { API_BASE_URL } from "../../../environment";

const formatAmount = (value) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(
    Number(value || 0)
  );

const NpaDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [bucket, setBucket] = useState("all");
  const [manager, setManager] = useState("all");
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedDates, setAppliedDates] = useState({ start_date: "", end_date: "" });

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const userInfo = JSON.parse(sessionStorage.getItem("info") || "{}");
        if (!userInfo.user_id) throw new Error("User ID is missing from the current session");

        const response = await axios.get(`${API_BASE_URL}/filter/npa_dashboard`, {
          params: {
            user_id: userInfo.user_id,
            ...(appliedDates.start_date && { start_date: appliedDates.start_date }),
            ...(appliedDates.end_date && { end_date: appliedDates.end_date }),
          },
        });
        setDashboard(response.data);
      } catch (requestError) {
        setError(requestError.response?.data?.error || requestError.message);
      } finally {
        setLoading(false);
      }
    };

    setLoading(true);
    setError("");
    fetchDashboard();
  }, [appliedDates]);

  const filteredCases = useMemo(() => {
    const cases = dashboard?.cases || [];
    const query = search.trim().toLowerCase();

    return cases
      .filter((item) => {
        const bucketMatches = bucket === "all" || item.bucket === bucket;
        const managerMatches = manager === "all" || String(item.manager_id || 0) === manager;
        const searchMatches = !query || [item.customer_name, item.phone_number, item.loan_number]
          .some((value) => String(value || "").toLowerCase().includes(query));
        return bucketMatches && managerMatches && searchMatches;
      })
      .sort((a, b) => {
        const aDate = a.oldest_due_date ? new Date(a.oldest_due_date).getTime() : 0;
        const bDate = b.oldest_due_date ? new Date(b.oldest_due_date).getTime() : 0;
        return bDate - aDate;
      });
  }, [dashboard, bucket, manager, search]);

  const summary = dashboard?.summary || {};

  const dateRangeLabel = appliedDates.start_date || appliedDates.end_date
    ? `${appliedDates.start_date || "Beginning"} to ${appliedDates.end_date || "Today"}`
    : "All dates";

  const exportRows = filteredCases.map((item) => [
    item.customer_name,
    item.phone_number || "",
    item.loan_number || item.loan_id,
    item.manager_name,
    item.overdue_emi_count,
    item.bucket,
    item.max_dpd,
    item.oldest_due_date ? new Date(item.oldest_due_date).toLocaleDateString("en-IN") : "",
    Number(item.overdue_amount || 0),
    item.npa_tagged ? "NPA" : "DPD",
  ]);

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(16);
    doc.text("NPA & DPD Dashboard", 14, 15);
    doc.setFontSize(10);
    doc.text(`Date range: ${dateRangeLabel}`, 14, 22);
    doc.text(
      `Total: ${summary.total_cases || 0}  |  Overdue: ${summary.overdue_cases || 0}  |  NPA: ${summary.npa_cases || 0}`,
      14,
      28
    );
    autoTable(doc, {
      startY: 34,
      head: [["Customer", "Phone", "Loan No.", "Manager", "Pending", "Bucket", "DPD", "Oldest Due", "Amount", "Tag"]],
      body: exportRows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [220, 53, 69] },
    });
    doc.save(`npa-dashboard-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.json_to_sheet([
      { Metric: "Date Range", Value: dateRangeLabel },
      { Metric: "All Loan Cases", Value: summary.total_cases || 0 },
      { Metric: "Overdue Cases", Value: summary.overdue_cases || 0 },
      { Metric: "2 EMI Pending", Value: summary.two_emi_cases || 0 },
      { Metric: "3-5 EMI Pending", Value: summary.three_to_five_emi_cases || 0 },
      { Metric: "6+ EMI Pending", Value: summary.six_plus_emi_cases || 0 },
      { Metric: "NPA Cases", Value: summary.npa_cases || 0 },
    ]);
    const casesSheet = XLSX.utils.aoa_to_sheet([
      ["Customer", "Phone", "Loan No.", "Manager", "Pending EMIs", "Bucket", "Max DPD", "Oldest Due", "Overdue Amount", "Tag"],
      ...exportRows,
    ]);
    const managerSheet = XLSX.utils.json_to_sheet((dashboard?.manager_performance || []).map((item) => ({
      Manager: item.manager_name,
      "Total Cases": item.total_cases,
      Overdue: item.overdue_cases,
      "Overdue Rate (%)": item.overdue_rate,
      "Average DPD": item.average_dpd,
      NPA: item.npa_cases,
      "Overdue Amount": Number(item.overdue_amount || 0),
    })));
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
    XLSX.utils.book_append_sheet(workbook, managerSheet, "Manager Performance");
    XLSX.utils.book_append_sheet(workbook, casesSheet, "Overdue Cases");
    XLSX.writeFile(workbook, `npa-dashboard-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
          <div>
            <h3 className="mb-1">NPA & DPD Dashboard</h3>
            <p className="text-muted mb-0">Prioritise overdue EMI cases and compare manager performance.</p>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <button className="btn btn-outline-danger" onClick={exportToPDF} disabled={!dashboard || loading}>
              <i className="ti ti-file-type-pdf me-1" /> PDF
            </button>
            <button className="btn btn-outline-success" onClick={exportToExcel} disabled={!dashboard || loading}>
              <i className="ti ti-file-spreadsheet me-1" /> Excel
            </button>
            <span className="badge bg-danger-subtle text-danger px-3 py-2">NPA = 90+ DPD</span>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex align-items-end flex-wrap gap-3">
              <div>
                <label className="form-label">From Date</label>
                <input type="date" className="form-control" value={startDate} max={endDate || undefined} onChange={(event) => setStartDate(event.target.value)} />
              </div>
              <div>
                <label className="form-label">To Date</label>
                <input type="date" className="form-control" value={endDate} min={startDate || undefined} onChange={(event) => setEndDate(event.target.value)} />
              </div>
              <button className="btn btn-primary" onClick={() => setAppliedDates({ start_date: startDate, end_date: endDate })}>Apply Filter</button>
              <button className="btn btn-light" onClick={() => {
                setStartDate("");
                setEndDate("");
                setAppliedDates({ start_date: "", end_date: "" });
              }}>Clear</button>
              <small className="text-muted ms-auto">Showing: {dateRangeLabel}</small>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {loading && <div className="text-center py-5">Loading overdue cases...</div>}

        {!loading && dashboard && (
          <>
            <div className="row g-3 mb-4">
              <SummaryCard label="All Loan Cases" value={summary.total_cases} color="primary" />
              <SummaryCard label="Overdue Cases" value={summary.overdue_cases} color="warning" />
              <SummaryCard label="2 EMI Pending" value={summary.two_emi_cases} color="info" />
              <SummaryCard label="3-5 EMI Pending" value={summary.three_to_five_emi_cases} color="orange" />
              <SummaryCard label="6+ EMI Pending" value={summary.six_plus_emi_cases} color="danger" />
              <SummaryCard label="NPA Cases" value={summary.npa_cases} color="dark" />
            </div>

            <div className="card mb-4">
              <div className="card-header"><h5 className="mb-0">Manager-wise DPD Performance</h5></div>
              <div className="card-body p-0 table-responsive">
                <table className="table table-hover mb-0">
                  <thead><tr><th>Manager</th><th>Total Cases</th><th>Overdue</th><th>Overdue Rate</th><th>Average DPD</th><th>NPA</th><th>Overdue Amount</th></tr></thead>
                  <tbody>
                    {dashboard.manager_performance.map((item) => (
                      <tr key={item.manager_id || "unassigned"}>
                        <td className="fw-semibold">{item.manager_name}</td>
                        <td>{item.total_cases}</td><td>{item.overdue_cases}</td>
                        <td><span className={`badge ${item.overdue_rate >= 50 ? "bg-danger" : "bg-warning text-dark"}`}>{item.overdue_rate}%</span></td>
                        <td>{item.average_dpd} days</td><td>{item.npa_cases}</td>
                        <td>{formatAmount(item.overdue_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <h5 className="mb-0">Overdue Case Worklist ({filteredCases.length})</h5>
                  <div className="d-flex flex-wrap gap-2">
                    <input className="form-control" style={{ width: 230 }} placeholder="Customer, phone or loan no." value={search} onChange={(event) => setSearch(event.target.value)} />
                    <select className="form-select" style={{ width: 165 }} value={bucket} onChange={(event) => setBucket(event.target.value)}>
                      <option value="all">All EMI buckets</option><option value="1 EMI">1 EMI</option><option value="2 EMI">2 EMI</option><option value="3-5 EMI">3-5 EMI</option><option value="6+ EMI">6+ EMI</option>
                    </select>
                    <select className="form-select" style={{ width: 180 }} value={manager} onChange={(event) => setManager(event.target.value)}>
                      <option value="all">All managers</option>
                      {dashboard.manager_performance.map((item) => <option key={item.manager_id || "unassigned"} value={String(item.manager_id || 0)}>{item.manager_name}</option>)}
                    </select>
                  </div>
                </div>
              </div>
              <div className="card-body p-0 table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead><tr><th>Customer</th><th>Loan No.</th><th>Manager</th><th>Pending EMIs</th><th>Max DPD</th><th>Oldest Due</th><th>Overdue Amount</th><th>Tag</th></tr></thead>
                  <tbody>
                    {filteredCases.map((item) => (
                      <tr key={item.emi_detail_id}>
                        <td><div className="fw-semibold">{item.customer_name}</div><small className="text-muted">{item.phone_number || "--"}</small></td>
                        <td>{item.loan_number || item.loan_id}</td><td>{item.manager_name}</td>
                        <td><span className="badge bg-warning-subtle text-warning">{item.overdue_emi_count} ({item.bucket})</span></td>
                        <td className="fw-semibold text-danger">{item.max_dpd} days</td>
                        <td>{item.oldest_due_date ? new Date(item.oldest_due_date).toLocaleDateString("en-IN") : "--"}</td>
                        <td>{formatAmount(item.overdue_amount)}</td>
                        <td>{item.npa_tagged ? <span className="badge bg-danger">NPA</span> : <span className="badge bg-secondary">DPD</span>}</td>
                      </tr>
                    ))}
                    {!filteredCases.length && <tr><td colSpan="8" className="text-center text-muted py-4">No overdue cases found for these filters.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, color }) => (
  <div className="col-xl-2 col-md-4 col-sm-6">
    <div className={`card h-100 border-start border-4 border-${color}`}>
      <div className="card-body"><div className="text-muted small mb-1">{label}</div><h3 className="mb-0">{Number(value || 0)}</h3></div>
    </div>
  </div>
);

export default NpaDashboard;
